import { from, Observable, throwError as observableThrowError } from 'rxjs';

import { catchError, switchMap, tap } from 'rxjs/operators';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { NotificationsService } from 'angular2-notifications';
import { CommonDataService } from 'src/sb-tools/typescript/services/common-data.service';
import { AuthenticationService } from 'src/sb-tools/typescript/authentication/authentication.service';
import { Error } from 'src/sb-tools/typescript/enums/error.enum';
import { TranslateService } from '@ngx-translate/core';
import { SignalRService } from './signalR.service';
import { environment } from '@environment';
import { AppInsightsService } from '@sb-tools/typescript/services/app-insights.service';
import { Location } from '@angular/common';
import { Router } from '@angular/router';

// import { AppInsightsService } from '@markpieszak/ng-application-insights';

@Injectable({ providedIn: 'root' })
export class Interceptor implements HttpInterceptor {
    constructor(
        private authenticationService: AuthenticationService,
        private notificationsService: NotificationsService,
        private translateService: TranslateService,
        private commonDataService: CommonDataService,
        private signalRService: SignalRService,
        private appInsightsService: AppInsightsService,
        private location: Location,
        private router: Router,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        let headers = req.headers.set('Version', environment.apiVersion);
        if (!this.commonDataService.isOneTime && this.isNeedAuthApi(req.url)) {
            console.log('%c intercept: ' + req.url, { color: 'red' });
            return from(
                this.authenticationService.getAuthorizationHeader(
                    this.authenticationService.community,
                    false,
                    'interceptor',
                ),
            ).pipe(
                switchMap((data) => {
                    if (typeof data !== 'string') {
                        return observableThrowError(data);
                    }

                    headers = headers.set('Authorization', data).set('Community', this.authenticationService.community);
                    if (
                        req.url.toLowerCase().indexOf(environment.apiInboxUrl.toLowerCase()) === 0 &&
                        this.signalRService.connection?.connectionId
                    ) {
                        headers = headers.set('SignalRConnectionId', this.signalRService.connection.connectionId);
                    }
                    //   .append('Content-Type', application/json');
                    const clonedRequest = req.clone({
                        headers,
                    });

                    // Pass the cloned request instead of the original request to the next handle
                    return next.handle(clonedRequest).pipe(
                        tap((event) => {
                            return this.setServerTime(event);
                        }),
                    );
                }),
                catchError((error) => {
                    return this.handleError(error, req);
                }),
            );
        } else if (this.commonDataService.isOneTime) {
            if (!req.url.includes('/customer/')) {
                headers = headers
                    .set('X-Token', this.commonDataService.xToken)
                    .set('Community', this.authenticationService.community);
            } else {
                headers = headers.set('Community', this.authenticationService.community);
            }

            //   .append('Content-Type', application/json');
            const clonedRequest = req.clone({
                headers,
            });

            // Pass the cloned request instead of the original request to the next handle
            return next.handle(clonedRequest).pipe(
                tap((event) => {
                    return this.setServerTime(event);
                }),
                catchError((response) => {
                    return this.handleError(response, req);
                }),
            );
        } else {
            // Pass the cloned request instead of the original request to the next handle
            const clonedRequest = req.clone({
                headers,
            });
            return next.handle(clonedRequest).pipe(
                tap((event) => {
                    return this.setServerTime(event);
                }),
                catchError((response) => {
                    return this.handleError(response, clonedRequest);
                }),
            );
        }
    }

    private handleError(response, req) {
        if (this.authenticationService.isLoggingOut) {
            return observableThrowError(response);
        }

        if (req.url.indexOf('oauth/token') !== -1) {
            this.authenticationService.refreshPromise = null;
        }

        if (!response.error) {
            response.error = {};
        }

        let message;
        if (response.status === 400 && req.url.indexOf('oauth/token') !== -1) {
            if (response.error.errorId === Error.sessionExpired) {
                this.appInsightsService.logEvent('Logoff', {
                    reason: 'error.sessionExpired',
                    file: 'interceptor.service.ts',
                    refreshToken: !!this.authenticationService.getRefreshToken(),
                    user: this.commonDataService.user?.id,
                });

                this.authenticationService.logOff({ isNeedRedirect: true });
                return observableThrowError(response);
            }

            if (response.error.errorId === Error.communityWithNameNotFound) {
                this.authenticationService.redirectToCommunities();
                return observableThrowError(response);
            }

            message = response.error['message'] || response.error['error_description'];
            if (message) {
                this.notificationsService.error(this.translateService.instant('Error!'), message);
            }

            this.appInsightsService.logEvent('Logoff', {
                reason: '400 error',
                file: 'interceptor.service.ts',
                refreshToken: !!this.authenticationService.getRefreshToken(),
                user: this.commonDataService.user?.id,
                error: response.error['message'] || response.error['error_description'],
            });

            this.authenticationService.logOff({ isNeedRedirect: true });
        } else if (
            response.status === 403 &&
            this.commonDataService.isOneTime &&
            response.error &&
            [Error.linkIsExpired].includes(response.error.errorId)
        ) {
            const url = this.location.path();

            this.router.navigate(['403'], {
                skipLocationChange: true,
            });

            this.location.replaceState(url);
        } else if (response.status === 401) {
            this.authenticationService.needToRefreshToken = true;
        } else if (response.error && response.error.errorId === Error.communityWithNameNotFound) {
            window.location.pathname = '/404';
        } else {
            if (response.error && (typeof response.error === 'string' || response.error.message)) {
                message = response.error && response.error.message ? response.error.message : response.error;
                if (message.toLowerCase().indexOf('timeout') >= 0) {
                    message = this.translateService.instant(
                        'Oops... your action was not completed due to a slow Internet connection. Please wait for a few seconds and try again.',
                    );
                }
            } else {
                message = this.translateService.instant(
                    'Oops... Something went wrong, please try again or reload this page',
                );
            }

            this.notificationsService.error(this.translateService.instant('Error!'), message);
        }

        return observableThrowError(response);
    }

    private isNeedAuthApi(requestUrl: string) {
        const requestUrlLower = requestUrl.toLocaleLowerCase();

        return (
            requestUrlLower.indexOf(environment.apiInboxUrl.toLocaleLowerCase()) === 0 ||
            requestUrlLower.indexOf(environment.apiMasterUrl.toLocaleLowerCase()) === 0 ||
            requestUrlLower.indexOf(environment.apiFormUrl.toLocaleLowerCase()) === 0 ||
            (environment.signalrServerUrl !== '' &&
                requestUrlLower.indexOf(environment.signalrServerUrl.toLocaleLowerCase()) === 0)
        );
    }

    private setServerTime(event) {
        if (event.type !== 0 && event['url'] && event['url'].indexOf(environment.apiMasterUrl) === 0) {
            const serverTime = event['headers'].get('date');
            this.authenticationService.setTimeDifference(serverTime);
        }
    }
}
